import React from 'react';
import { useTranslation } from 'react-i18next';

interface StageMapProps {
  visibleStages: number[];
  counts: Record<number, number>;
  collapsed: Record<number, boolean>;
  onJump: (stage: number) => void;
}

/**
 * Horizontal "you are here" map of the 5 healing stages.
 * Sticky under the navbar so readers never lose the structure of the archive
 * while scrolling long stage sections.
 */
const StageMap: React.FC<StageMapProps> = ({ visibleStages, counts, collapsed, onJump }) => {
  const { t } = useTranslation('therapyLibrary');

  if (visibleStages.length === 0) return null;

  return (
    <nav
      aria-label={t('stageMap.label', { defaultValue: 'Healing stage map' })}
      className="sticky top-16 z-30 -mx-4 px-4 py-3 bg-[#FDFAF6]/95 backdrop-blur-sm border-y border-[hsl(var(--primary)/0.18)]"
    >
      <ol className="flex items-stretch gap-2 overflow-x-auto no-scrollbar">
        {visibleStages.map((n) => (
          <li key={n} className="flex-shrink-0">
            <button
              type="button"
              onClick={() => onJump(n)}
              className={`h-full text-left px-3 py-2 rounded-sm border transition-colors font-sans min-w-[9.5rem] ${
                collapsed[n]
                  ? 'border-stone-200 bg-transparent hover:border-[hsl(var(--primary)/0.4)]'
                  : 'border-[hsl(var(--primary)/0.45)] bg-[hsl(var(--primary)/0.10)]'
              }`}
            >
              <span className="block text-[9px] uppercase tracking-[0.25em] text-stone-500">
                {t('stageLabel', { defaultValue: 'Stage' })} {n}
              </span>
              <span className="block text-xs font-medium text-stone-800 leading-snug mt-0.5 line-clamp-2">
                {t(`stages.${n}.title`)}
              </span>
              <span className="block text-[10px] text-stone-500 mt-1">{counts[n] || 0}</span>
            </button>
          </li>
        ))}
      </ol>
    </nav>
  );
};

export default StageMap;
